/** App config
 * - define idp endpoints, tenants name etc for each environment
 * - then compute MSAL config from it
 *
 * @example app.config.ts
 * ```ts
 * import { environment } from '@env';
 * import { provideEnv, getAuthProviders } from '@evc/platform';
 *
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     provideEnv(environment),
 *     ...getAuthProviders(environment),
 *   ],
 * };
 * ```
 */
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import type { Provider, WritableSignal } from '@angular/core';
import { inject, InjectionToken, signal } from '@angular/core';
import type { MsalGuardConfiguration, MsalInterceptorConfiguration } from '@azure/msal-angular';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalService,
} from '@azure/msal-angular';
import type { IPublicClientApplication, NavigationOptions } from '@azure/msal-browser';
import {
  BrowserCacheLocation,
  InteractionType,
  LogLevel,
  NavigationClient,
  PublicClientApplication,
} from '@azure/msal-browser';

import type { DeepPartial, Maybe } from '@evc/web-components';
import { deepMerge, ENV_VARIABLES } from '@evc/web-components';

import type { PlatformEnv } from '../../../../features/config/env.type';
import { LanguageService } from '../../../../features/i18n/language.service';
import { AUTH_REQUEST_TYPE, authRequestFactory, getAuthConfig } from '../auth.config';
import type { AuthConfig } from '../auth.type';
import { AuthInterceptor } from '../interceptor/auth.interceptor';

/* way to inject config from a callback to pass .env config : see getAuthProviders()*/
export const AUTH_CONFIG = new InjectionToken<string>('AUTH_CONFIG');

/** Organization id token (cannont inject authservice here (DI circular)) */
export const TOKEN_CLAIM_ORGANIZATION_ID = new InjectionToken<WritableSignal<Maybe<string>>>(
  'TOKEN_CLAIM_ORGANIZATION_ID',
);

/** Deep-link to restore once MSAL interaction is fully done (see AuthService#handleInteractionProgress). */
export const POST_LOGIN_REDIRECT_URL = new InjectionToken<WritableSignal<Maybe<string>>>(
  'POST_LOGIN_REDIRECT_URL',
);

/** generate needed providers for MSAL - to be imported in app.config.ts */
export function getAuthProviders(
  environment?: PlatformEnv & Record<string, unknown>,
  config?: DeepPartial<AuthConfig>,
): Provider[] {
  if (!environment?.USE_CONNECT_PLATFORM) return [];

  return [
    {
      provide: AUTH_CONFIG,
      useFactory() {
        const authConfig = getAuthConfig(environment);
        const mergedConfig = deepMerge(authConfig, config ?? {});

        if (environment.DEBUG_AUTH_FLOW) {
          const { redirects } = mergedConfig || {};
          // drive unexpected issue : `Spread types may only be created from object types.`
          const speadableRedirects = typeof redirects === 'object' ? redirects : {};
          // eslint-disable-next-line no-console
          console.debug(
            '[auth] provider',
            { authConfig, config, environment },
            { ...mergedConfig },
            { ...speadableRedirects },
          );
        }

        return mergedConfig;
      },
      deps: [ENV_VARIABLES],
    },
    {
      provide: TOKEN_CLAIM_ORGANIZATION_ID,
      useFactory: () => signal<Maybe<string>>(undefined),
    },
    {
      provide: POST_LOGIN_REDIRECT_URL,
      useFactory: () => signal<Maybe<string>>(undefined),
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
      deps: [ENV_VARIABLES, AUTH_CONFIG],
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
      deps: [ENV_VARIABLES, AUTH_CONFIG],
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
      deps: [ENV_VARIABLES, AUTH_CONFIG],
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
  ];
}

/** Override internal navigation after redirect:
 * - Same-route navigation (auth response cleanup): use replaceState only, avoiding a reload.
 * - Different-route navigation (navigateToLoginRequestUrl): store the target URL instead of navigating immediately.*/
class SpaNavigationClient extends NavigationClient {
  constructor(private readonly postLoginRedirectUrl: WritableSignal<Maybe<string>>) {
    super();
  }

  override async navigateInternal(url: string, _options: NavigationOptions): Promise<boolean> {
    const target = new URL(url, window.location.origin);
    const isSameRoute =
      target.pathname === window.location.pathname && target.search === window.location.search;

    window.history.replaceState(null, '', url);

    if (!isSameRoute) {
      this.postLoginRedirectUrl.set(target.pathname + target.search + target.hash);
    }

    return false;
  }
}

function MSALInstanceFactory(
  environment: PlatformEnv,
  config: AuthConfig,
): IPublicClientApplication {
  const postLoginRedirectUrl = inject(POST_LOGIN_REDIRECT_URL);
  const { ENV } = environment ?? {};

  const {
    clientId,
    redirects,
    b2cPolicies,
    onRedirectNavigate,
    navigateToLoginRequestUrl,
    authorityDomain,
  } = config ?? {};

  const logLevel = (() => {
    switch (ENV) {
      case 'production':
        return LogLevel.Error;
      case 'staging':
        return LogLevel.Warning;
      default:
        return LogLevel.Verbose;
    }
  })();

  return new PublicClientApplication({
    auth: {
      authority: b2cPolicies.authorities.signUpSignIn as string,
      knownAuthorities: [...(authorityDomain ? [authorityDomain] : [])],
      clientId,
      redirectUri: redirects.success, // current app
      postLogoutRedirectUri: redirects.logout, // public home
      navigateToLoginRequestUrl,
      onRedirectNavigate,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      navigationClient: new SpaNavigationClient(postLoginRedirectUrl),
      allowRedirectInIframe: true, // Needed for Logout Front Channel
      allowPlatformBroker: false, // Disables WAM Broker
      loggerOptions: {
        logLevel,
        piiLoggingEnabled: true,
        // loggerCallback: (level, message, containsPii) => {
        //   // eslint-disable-next-line no-console
        //   console.log(`[MSAL] ${level}: ${message}`, containsPii ? '[PII]' : '');
        // },
      },
      // sso silent try using iframe but hit error due to X-Frame-Options: deny
      // - reduce timeout so try interactive fallback sooner (default = 6000)
      iframeHashTimeout: 2000,
    },
  });
}

function MSALInterceptorConfigFactory(
  environment: PlatformEnv,
  config: AuthConfig,
): MsalInterceptorConfiguration {
  const getTargetOrganizationId = inject(TOKEN_CLAIM_ORGANIZATION_ID);
  const languageService = inject(LanguageService);
  languageService.init();

  const PROTECTED_URIS = environment.PROTECTED_URIS ?? {};
  const { scopes } = config ?? {};
  const protectedResourceMap = new Map<string, string[]>();

  Object.values(PROTECTED_URIS)
    .filter(Boolean)
    .forEach((uri) => protectedResourceMap.set(uri, scopes));

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
    authRequest: () =>
      authRequestFactory(AUTH_REQUEST_TYPE.INDETERMINATE, {
        triggerType: 'interceptor',
        environment,
        config,
        lang: languageService.platformSafeLang(),
        toid: getTargetOrganizationId(),
      }),
  };
}

function MSALGuardConfigFactory(
  environment: PlatformEnv,
  config: AuthConfig,
): MsalGuardConfiguration {
  const getTargetOrganizationId = inject(TOKEN_CLAIM_ORGANIZATION_ID);
  const languageService = inject(LanguageService);
  languageService.init();
  const { redirects } = config ?? {};

  const configuration: MsalGuardConfiguration = {
    interactionType: InteractionType.Redirect,
    loginFailedRoute: redirects?.loginFailedRoute,
    authRequest: () =>
      authRequestFactory(AUTH_REQUEST_TYPE.INDETERMINATE, {
        triggerType: 'guard',
        environment,
        config,
        lang: languageService.platformSafeLang(),
        toid: getTargetOrganizationId(),
      }),
  };

  return configuration;
}

export default getAuthProviders;
